import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { T } from "theme/tokens";

/**
 * The dashboard's DataGrid preset.
 *
 * Exists because every table in this app previously restyled the grid inline
 * and each one handled loading, emptiness and failure slightly differently —
 * several not at all, so a failed fetch looked identical to a pharmacy with
 * no orders. Those three states are explicit here.
 *
 * Server-side paging is the default: these tables sit in front of collections
 * with hundreds of thousands of rows, and client-side paging would pull all
 * of them.
 */
export default function DataTable({
  rows = [],
  columns,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = "Nothing to show yet.",
  rowCount,
  paginationModel,
  onPaginationModelChange,
  getRowId,
  onRowClick,
  height = 560,
  sx,
  ...rest
}) {
  if (error) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          border: `1px solid ${T.border}`,
          borderRadius: 3,
          bgcolor: T.surface,
        }}
      >
        <Typography sx={{ color: T.error, fontWeight: 600 }}>{error}</Typography>
        {onRetry && (
          <Button size="small" variant="outlined" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Box>
    );
  }

  // An empty grid renders as a bare header row, which reads as broken. Only
  // show this once loading has finished, so it never flashes during a fetch.
  if (!loading && !rows.length) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          border: `1px solid ${T.border}`,
          borderRadius: 3,
          bgcolor: T.surface,
        }}
      >
        <Typography sx={{ color: T.muted }}>{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, width: "100%", position: "relative", ...sx }}>
      {loading && !rows.length && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <CircularProgress size={28} sx={{ color: T.primary }} />
        </Box>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        onRowClick={onRowClick}
        rowCount={rowCount}
        paginationMode={rowCount === undefined ? "client" : "server"}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        // Row heights vary with two-line cells; letting the grid measure keeps
        // addresses and names from being clipped.
        getRowHeight={() => "auto"}
        sx={{
          border: `1px solid ${T.border}`,
          borderRadius: 3,
          bgcolor: T.surface,
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: T.borderSoft,
            borderBottom: `1px solid ${T.border}`,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: "0.7rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: T.muted,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${T.borderSoft}`,
            fontSize: "0.85rem",
            color: T.ink,
            py: 1,
            alignItems: "center",
          },
          "& .MuiDataGrid-row:hover": { bgcolor: T.borderSoft },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `1px solid ${T.border}`,
          },
          ...(onRowClick && { "& .MuiDataGrid-row": { cursor: "pointer" } }),
        }}
        {...rest}
      />
    </Box>
  );
}
